import { Anchor, Box, Group, Popover } from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { OcrAnnotationInfo } from '@/components/annotations/OcrAnnotationInfo/OcrAnnotationInfo'
import { LoadingOverlay } from '@/components/ui-shared/LoadingOverlay/LoadingOverlay'
import {
  useCreateDataset,
  useInvalidateDatasetQueries
} from '@/queries/datasetQueries'
import { showToast } from '@/theme/notifications'
import { Dataset } from '@/types/dataset'
import { useCloneDatasetHandler } from '../CloneDatasetHandler/useCloneDatasetHandler'
import { DatasetForm, FormValues } from '../DatasetForm/DatasetForm'

export type DatasetCreationResult = {
  actionType: 'create' | 'clone'
  dataset: Dataset
}

type CreateDatasetHandlerProps = {
  initialValues?: Partial<FormValues>
  onCancel: () => void
  onDatasetCreated: (params: DatasetCreationResult) => void
}

export const CreateDatasetHandler = ({
  initialValues,
  onCancel,
  onDatasetCreated
}: CreateDatasetHandlerProps) => {
  const intl = useIntl()

  const { mutateAsync: createDataset, isPending: isCreating } =
    useCreateDataset()

  const { cloneDataset, isPending: isCloning } = useCloneDatasetHandler()

  const { invalidateDatasetList } = useInvalidateDatasetQueries()

  const handleCreateDataset = async (values: FormValues) => {
    const { name, description, datasetType, labels, labelingType } = values

    try {
      const dataset = await createDataset({
        name,
        description,
        dataset_type: datasetType,
        labels: labels.map((label) => ({
          name: label.name,
          color: label.color
        })),
        labeling_type: labelingType,
        is_library: false
      })

      showToast(
        intl.formatMessage({ id: 'datasets.create.success' }),
        'success'
      )

      void invalidateDatasetList()

      onDatasetCreated({
        actionType: 'create',
        dataset
      })
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'datasets.create.error' })
        ),
        'error'
      )
    }
  }

  const handleSubmit = (values: FormValues) => {
    if (values.shouldCloneDataset) {
      void cloneDataset(values, {
        onSuccess: (dataset) => {
          void invalidateDatasetList()
          onDatasetCreated({ actionType: 'clone', dataset })
        }
      })
    } else {
      void handleCreateDataset(values)
    }
  }

  return (
    <Box pos="relative">
      <LoadingOverlay
        visible={isCreating || isCloning}
        message={isCloning && <FormattedMessage id="datasets.clone.pending" />}
      />

      <DatasetForm
        initialValues={initialValues}
        submitLabel={<FormattedMessage id="datasets.create" />}
        bottomContent={
          <Popover width={480} position="bottom-start" withArrow>
            <Popover.Target>
              <Group align="center" gap={4} wrap="nowrap">
                <IconInfoCircle size={16} />

                <Anchor type="button" size="xs" underline="always">
                  <FormattedMessage id="annotation.ocr.banner.toggle" />
                </Anchor>
              </Group>
            </Popover.Target>

            <Popover.Dropdown>
              <OcrAnnotationInfo />
            </Popover.Dropdown>
          </Popover>
        }
        withClone
        onCancel={onCancel}
        onSubmit={handleSubmit}
      />
    </Box>
  )
}
